import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Copy, Check, Mail } from "lucide-react";
import { toast } from "sonner";

const SUPPORT_EMAIL = "support@clientflow.no";

/**
 * Small "contact support" dialog used from the sidebar/mobile menu.
 *
 * The MVP rule: don't rely on `mailto:` alone, because users with no default
 * mail app see nothing happen when they click. This dialog gives them two
 * always-working paths:
 *   1. "Åpne e-post" → the same prefilled mailto we already had.
 *   2. "Kopier e-postadresse" → copies support@clientflow.no to clipboard
 *      so they can paste it into Gmail/Outlook/etc. on the web.
 *
 * `mailtoHref` is built upstream in AppLayout so it can include the business
 * name, the logged-in user's email, and the current page URL.
 */
export default function SupportDialog({ open, onOpenChange, mailtoHref }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(SUPPORT_EMAIL);
      setCopied(true);
      toast.success("E-postadresse kopiert");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Kunne ikke kopiere — marker adressen manuelt.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md rounded-2xl"
        data-testid="support-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-2">
            <Mail className="w-5 h-5 text-primary" strokeWidth={1.75} />
            Trenger du hjelp?
          </DialogTitle>
          <DialogDescription>
            Send oss en e-post, så hjelper vi deg.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-xl border border-border bg-accent/40 px-4 py-3 flex items-center justify-between gap-3">
          <span
            className="font-mono text-sm text-foreground select-all break-all"
            data-testid="support-email-text"
          >
            {SUPPORT_EMAIL}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="shrink-0 rounded-lg"
            onClick={copy}
            data-testid="support-copy-inline"
            aria-label="Kopier e-postadresse"
          >
            {copied ? (
              <Check className="w-4 h-4 text-primary" strokeWidth={2} />
            ) : (
              <Copy className="w-4 h-4 text-muted-foreground" strokeWidth={1.75} />
            )}
          </Button>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            type="button"
            variant="outline"
            className="rounded-xl"
            onClick={copy}
            data-testid="support-copy-btn"
          >
            <Copy className="w-4 h-4 mr-2" strokeWidth={1.75} />
            Kopier e-postadresse
          </Button>
          <Button
            asChild
            className="rounded-xl"
            data-testid="support-open-mail-btn"
          >
            <a href={mailtoHref}>
              <Mail className="w-4 h-4 mr-2" strokeWidth={1.75} />
              Åpne e-post
            </a>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
